// @ts-check
'use_strict';
import { AddressBook } from '../tv_address_book/address_book';
import { ToggleContent } from '../tv_utils/toggle_content';
import { FlexGridBoxes } from '../tv_utils/flex_grid_boxes';
import { IAddress } from './IAddress';
import { CarrierLocationSelector } from './CarrierLocationSelector';
import { ShippingEstimator } from './ShippingEstimator';
export declare var $;

/**
 * Abstract Base Class to house most of the shipping estimator logic for both the cart and product page.
 */

export abstract class BaseShippingEstimatorView {
    /**
     * @var {ShippingEstimator} shipping_estimator calls out and runs shipping estimates.
     * @var {FlexGridBoxes} production_flex_grid controls to select the production option.
     * @var {{[key: string]: any}} elements relevant on the DOM.
     * @var {string} production_code The code used for production to get correct delivery dates.
     * @var {IAddress} address The current selected address to run shipping estimates on.
     * @var {AddressBook} address_book The address book, if the user is logged in.
     * @var {CarrierLocationSelector} carrier_location_selector to select carrier locations.
     * @var {ToggleContent} tabs The tabs controller to swap modal content.
     * @var {boolean} estimator_opened Whether or not the estimator modal has been opened yet.
     * @var {boolean} production_options_visible Flag.
     * @var {boolean} shipping_options_visible Flag.
     * @var {number} throttle reference to set timeout to stop change events from firing too fast.
     */
    shipping_estimator: ShippingEstimator;
    production_flex_grid: FlexGridBoxes;
    elements: { [key: string]: any; };
    production_code: string;
    production_price: number;
    address: IAddress;
    address_book: AddressBook;
    carrier_location_selector: CarrierLocationSelector;
    tabs: ToggleContent;
    estimator_opened: boolean;
    production_options_visible: boolean = false;
    shipping_options_visible: boolean = false;
    throttle: number;

    /**
     * Instantiate the shipping estimator class.
     *
     * @return {ShippingEstimator} instantiated and bound.
     */
    constructor() {
        this.elements = {};
        this.production_code = 'S';
        this.estimator_opened = false;
        this.shipping_estimator = new ShippingEstimator(null, true, this.resize.bind(this));
        this.address_book = new AddressBook('delivery', this.address_book_callback.bind(this));
        this.carrier_location_selector = new CarrierLocationSelector(this.resize.bind(this));

        // Set the address.
        const address_book_address = this.address_book.address();
        if (!address_book_address.zipcode) {
            this.address = {
                name: '',
                company: '',
                email: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zipcode: '60652',
                country: 'US',
                phone: '',
                recid: '',
            };
        } else {
            this.address = address_book_address;
        }

        // Bind input events.
        this.bind();

        // Hide the full address button, since it's not needed.
        if (this.elements.address_book_full_address_button) {
            this.elements.address_book_full_address_button.classList.add('hidden');
        }

        // By default, just show the input form for addresses.
        this.address_book.toggle_inputs(true);
        document.querySelectorAll('.js-shipping-address-input').forEach(elem => {
            elem.classList.remove('hidden');
        });

        this.render_production_times();

        // Finally, just resize and re-center.
        this.resize();
    }

    /**
     * Finds all of the elements needed for this view to run.
     *
     * @returns {void}
     */
    protected abstract find_elements(): void;

    /**
     * Binds events for buttons on the shipping estimation modal.
     *
     * @returns {void}
     */
    protected bind(): void {
        this.find_elements();

        // Set the address zipcode value from the address form zipcode input.
        // This is done when the user manually types in their address from the estimate.
        this.elements.address_zipcode_input.addEventListener('keyup', () => {
            this.elements.zipcode_input.value = this.elements.address_zipcode_input.value;
            this.address.zipcode = this.elements.zipcode_input.value;
        });

        this.elements.zipcode_input.addEventListener('keyup', this.zipcode_input.bind(this));
        this.elements.zipcode_input.addEventListener('change', this.zipcode_input.bind(this));

        if (this.elements.zipcode_input_button) {
            this.elements.zipcode_input_button.addEventListener('click', (e) => {
                this.fetch_address();
                this.toggle_required_fields(true, true);

                if (!e.target.form.checkValidity()) {
                    e.stopPropagation();
                    e.preventDefault();
                    e.target.form.reportValidity();
                    return;
                }

                this.estimate_back_to_shipping_summary();
            });
        }
        // When selecting an address, send the user back to the shipping summary in the estimate.
        if (this.elements.use_address_button) {
            this.elements.use_address_button.addEventListener('click', () => {
                this.estimate_back_to_shipping_summary();
            });
        }

        document.addEventListener('click', this.handle_page_click_events.bind(this));

        // The link to see more production options.
        this.elements.see_more_production_options_link = document.getElementById('js-see-more-production-options');
        // The link to see more shipping options.
        this.elements.see_more_shipping_options_link = document.getElementById('js-btn-see-more');
    }

    /**
     * Called when the open dialog link is clicked.
     *
     * @returns {void}
     */
    protected opened(): void {
        // Need to bind the elements before fetching estimates; otherwise if we are on the product page and
        // something in the config has changed we might not have the right data such as production code or price.
        this.find_elements();

        this.estimator_opened = true;
        this.production_flex_grid.locked = true;
        this.shipping_estimator.bind_boxing_data();
        this.toggle_inputs();
        this.shipping_estimator.fetch_estimates(this.address, this.production_code).then(() => {
            this.production_flex_grid.locked = false;
            this.toggle_inputs();
        });

        this.find_elements();
        this.render_production_times();

    }

    /**
     * Handles delegate click events, where the element may not be on the DOM yet.
     *
     * @param {Event} event The click event to dig through.
     * @return {void}
     */
    protected handle_page_click_events(event: Event): void {
        const elem = <HTMLInputElement>event.target;

        if (document.getElementById('js-delivery-address-error')) {
            document.getElementById('js-delivery-address-error').classList.add('tv-display__none');
        }

        // A carrier location was selected.
        if (elem.classList.contains('js-carrier_location_selector')) {
            const target: HTMLElement = <HTMLElement>event.target;
            this.address = {
                name: '',
                company: '',
                email: '',
                address1: target.dataset.address1,
                address2: target.dataset.address2,
                city: target.dataset.city,
                state: target.dataset.state,
                zipcode: target.dataset.zip,
                country: target.dataset.country,
                phone: '',
                recid: ''
            };
            this.elements.zipcode_input.value = target.dataset.zip;
            this.estimate_back_to_shipping_summary();
        }

        // Check to see if a section needs to be displayed.
        if (elem.dataset.type === 'production') { // Production Times section.
            event.preventDefault();
            this.toggle_required_fields(false, false);
            this.production_options_visible = !this.production_options_visible;
            this.estimate_display_production_options();
        } else if (elem.dataset.type === 'shipping') { // Shipping Options section.
            this.estimate_display_shipping_options();
        } else if (elem.dataset.type === 'address' || elem.dataset.type === 'carrier-locations') {
            this.estimate_display_address_or_carrier_locations(elem);
        } else if (elem.dataset.type === 'back') {
            this.estimate_back_to_shipping_summary();
        } else if (elem.parentElement && elem.parentElement.classList.contains('js-production-time-option')) {
            this.estimate_back_to_shipping_summary();
        }

        // Defocus address inputs if other elements are clicked
        const inputs = Array.from(this.elements.address_zipcode_input.form.elements);
        if (!inputs.includes(elem)) {
            this.blur_inputs();
        }

    }

    /**
     * Fetch address from form
     *
     * @return {void}
     */
    protected fetch_address(): void {
        this.address = {
            name: this.elements.name ? this.elements.name.value : '',
            company: '',
            email: '',
            address1: this.elements.address1.value,
            address2: '',
            city: this.elements.city.value,
            state: this.elements.state.value,
            zipcode: this.elements.zipcode_input.value,
            country: this.elements.country.value,
            phone: this.elements.phone ? this.elements.phone.value : '',
            recid: '',
        };
    }

    /**
     * Method to remove focus from inputs
     *
     * @return {void}
     */
    protected blur_inputs(): void {
        const form_elements = this.elements.address_zipcode_input.form.elements;
        for (const key in form_elements) {
            if (form_elements[key].tagName === 'INPUT' || form_elements[key].tagName === 'SELECT') {
                form_elements[key].blur();
            }
        }
    }

    /**
     * Method to toggle Inputs Disabled attribute.
     *
     * @return {void}
     */
    protected toggle_inputs(): void {
        // Catch all form inputs.
        const form_elements = this.elements.address_zipcode_input.form.elements;
        for (const key in form_elements) {
            if ((form_elements[key].tagName === 'INPUT' && form_elements[key].type !== 'hidden') ||
                form_elements[key].tagName === 'SELECT') {
                if (form_elements[key].disabled) {
                    form_elements[key].removeAttribute('disabled');
                } else {
                    form_elements[key].disabled = true;
                }
            }
        }
    }

    /**
     * Toggle required attribute
     *
     * @param {boolean} required if its a required field
     * @param {boolean} estimate if required for estimation or not.
     * @return {void}
     */
    protected toggle_required_fields(required: boolean, estimate: boolean): void {
        const required_fields = [
            'name',
            'phone',
            'address1',
            'city',
            'state',
            'country',
            'zipcode'
        ];

        for (const field of required_fields) {
            const field_name = (field == 'zipcode') ? 'zipcode_input' : field;
            if (this.elements[field_name]) {
                if (!required) {
                    this.elements[field_name].removeAttribute('required');
                } else {
                    this.elements[field_name].setAttribute('required', 'required');
                }
            }
        }
        // If required attribute gets set for name and phone,
        // we need to remove it for estimation.
        if (estimate) {
            const not_required = required_fields.slice(0, 2);
            for (const field of not_required) {
                if (this.elements[field]) {
                    this.elements[field].removeAttribute('required');
                }
            }
        }
    }

    /**
     * Save the address to address book
     *
     * @return {void}
     */
    protected save_to_address_book(): void {
        const required_fields = [
            'name',
            'phone',
            'address1',
            'city',
            'state',
            'country',
            'zipcode'
        ];
        // Validate required fields
        if (!this.validate_required_fields(required_fields)) {
            return;
        }

        this.address_book.save();

        if (document.getElementById('js-new-edit-container')) {
            document.getElementById('js-new-edit-container').style.display = 'block';
        }

        if (document.getElementById('shipping_error')) {
            document.getElementById('shipping_error').classList.add('hidden');
        }
    }

    /**
     * Display the production options on the estimate page.
     *
     * @return {void}
     */
    protected estimate_display_production_options(): void {
        if (this.production_options_visible) {
            this.elements.see_more_production_options_link.innerHTML = 'Hide options';
        } else {
            this.elements.see_more_production_options_link.innerHTML = 'See more options';
        }
        this.resize();
    }

    /**
     * Display the shipping options on the estimate page.
     *
     * @return {void}
     */
    protected estimate_display_shipping_options(): void {
        if (this.shipping_estimator.elements.selected_shipping_option.innerHTML !== '') {
            let dipslay = 'block';
            if (this.elements.see_more_shipping_options_link.classList.contains('selected')) {
                dipslay = 'none';
                this.elements.see_more_shipping_options_link.classList.remove('selected');
                this.elements.see_more_shipping_options_link.innerHTML = 'See more options';
            } else {
                this.elements.see_more_shipping_options_link.classList.add('selected');
                this.elements.see_more_shipping_options_link.innerHTML = 'Hide options';
            }
            this.shipping_estimator.elements.results_container.style.display = dipslay;
            this.shipping_estimator.elements.shipping_results.style.display = dipslay;

            // Highlight the default selected.
            const selected_option = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName(
                'js-shipping-rate-choose-button'
            );
            const selected_shipping = <HTMLInputElement>document.getElementById('js-selected-shipping-product');
            if (selected_shipping.value !== '') {
                const selected_shipping_option = JSON.parse(selected_shipping.value);
                let count = 0;
                for (const option of selected_option) {
                    const rec_name = JSON.parse(option.dataset.rec);
                    option.classList.remove('tv-flex-grid-box__selected');
                    if (rec_name.name == selected_shipping_option.name && count === 0) {
                        option.classList.add('tv-flex-grid-box__selected');
                        count += 1;
                    }
                }
            }

            this.resize();
        }
    }

    /**
     * Display the address or carrier locations section on the estimate page.
     *
     * @param {HTMLElement} elem is the element that was clicked.
     * @return {void}
     */
    protected estimate_display_address_or_carrier_locations(elem: HTMLElement): void {
        this.elements.production_and_shipping_main_container.classList.add('hidden');
        this.elements.address_and_carrier_locations_container.classList.remove('hidden');

        for (const container of this.elements.address_carrier_container) {
            container.classList.add('hidden');
        }

        if (elem.dataset.type === 'address') {
            this.elements.address_container.style.display = 'block';
        }
        else if (elem.dataset.type === 'carrier-locations') {
            const input_zipcode = this.elements.zipcode_input.value;
            if (input_zipcode.length >= 5) {
                this.address.zipcode = input_zipcode;
            }
            this.carrier_location_selector.fetch_locations(this.address.zipcode);
            this.elements.carrier_locations_container.style.display = 'block';
        }

        this.resize();
    }

    /**
     * Display the shipping summary section on the estimate page.
     *
     * @return {void}
     */
    protected estimate_back_to_shipping_summary(): void {
        this.production_flex_grid.locked = true;
        this.fetch_address();

        const required_fields = [
            'address1',
            'city',
            'state',
            'country',
            'zipcode'
        ];

        // Validate required fields
        if (!this.validate_required_fields(required_fields)) {
            return;
        }
        // Bind boxing data again, if any update made to configurator.
        this.shipping_estimator.bind_boxing_data();
        this.toggle_inputs();
        this.shipping_estimator.fetch_estimates(this.address, this.production_code).then(shipping_methods => {
            this.production_flex_grid.locked = false;

            // Unhide Shipping Estimator Header
            document.getElementById('tv-shipping-est-header').classList.remove('tv-display__none');

            // Generate Shipping Estimator table.
            let js_shipping_rows = document.getElementsByClassName('js-shipping-table-row');
            let js_shipping_radio_btn = document.getElementsByClassName('js-shipping-radio-btn');
            const js_rec_options = document.getElementById('js-shipping-rec-options-btn');
            const js_ups_options = document.getElementById('js-shipping-ups-btn');
            const js_fedex_options = document.getElementById('js-shipping-fedex-btn');
            const js_usps_options = document.getElementById('js-shipping-usps-btn');
            const all_shipping_estimates: Array<HTMLElement> = (this.shipping_estimator.flex_grid &&
               Array.from(this.shipping_estimator.flex_grid.elements)) || [];
            const shipping_estimates_data = all_shipping_estimates.map(est => JSON.parse(est.dataset.rec));

            document.getElementById('js-shipping-times-container').innerHTML = "";

            for (const shipping_estimate_lines of shipping_estimates_data) {
                const display_name = shipping_estimate_lines.maskName ?? shipping_estimate_lines.name;
                document.getElementById('js-shipping-times-container').innerHTML +=
                    `<tr height='35' class='js-production-table-row js-shipping-table-row'><td>
                    <input type='radio' class='js-shipping-radio-btn'></td><td>
                    ${shipping_estimate_lines.estimatedDeliveryDate} by
                    ${shipping_estimate_lines.estimatedDeliveryTime} </td><td class="display-shipping-amount">
                    $${shipping_estimate_lines.amount.toFixed(2)} </td><td>
                    ${display_name}
                </td></tr>`;
                if (shipping_estimate_lines.carrier === 'UPS') {
                    js_ups_options.classList.remove('tv-display__none');
                }
                if (shipping_estimate_lines.carrier === 'FedEx') {
                    js_fedex_options.classList.remove('tv-display__none');
                }
            }

            const display_shipping_amount = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName(
                'display-shipping-amount'
            );

            for (let i = 0; i < display_shipping_amount.length; i++) {
                if (display_shipping_amount[i].innerText == "$0.00") {
                    if (shipping_estimates_data[i].displayZeroPrice) {
                        display_shipping_amount[i].innerText = "FREE";
                    }
                }
            }

            // The number of recommended options is the number of options with masked names.
            const recommended_count = shipping_estimates_data.filter(d => d.maskName).length;

            for (let i = 0; i < js_shipping_rows.length; i++) {

                // Hide non-recommended
                if (i >= recommended_count) {
                    js_shipping_rows[i].classList.add('tv-display__none');
                } else {
                    js_shipping_rows[i].classList.add(i % 2 ? 'tv-zebra-table__even' : 'tv-zebra-table__odd');
                }

                // First row selected by default
                if (i == 0) {
                    js_shipping_rows[0].classList.add('tv-flex-grid-box__selected');
                    js_shipping_radio_btn[0].setAttribute('checked', '');
                }

                // Fix disabled radio buttons
                js_shipping_radio_btn[i].setAttribute('disabled', 'disabled');

                document.getElementById('js-shipping-times-container').classList.add('tv-zebra-table');

            }

            // Click functionality for each row class
            for (let i = 0; i < js_shipping_rows.length; i++) {
                js_shipping_rows[i].addEventListener('click', function () {
                    // Activate hidden shipping production elements on click
                    Array.from(js_shipping_rows).forEach(js_shipping_rows => {
                        js_shipping_rows.classList.remove('tv-flex-grid-box__selected');
                    });
                    Array.from(js_shipping_radio_btn).forEach(js_shipping_radio_btn => {
                        js_shipping_radio_btn.removeAttribute('checked');
                    });
                    js_shipping_rows[i].classList.add('tv-flex-grid-box__selected');
                    js_shipping_radio_btn[i].setAttribute('checked', '');
                    (<HTMLElement>document.getElementsByClassName('tv-flex-grid-box')[i]).click();
                });
            }

            js_rec_options.classList.add('tv-shipping-selected');

            // Recommended Options.
            document.getElementById('shipping-options').classList.remove('tv-display__none');
            document.getElementById('tv-shipping-table-head').classList.remove('tv-display__none');
            js_rec_options.addEventListener('click', function () {
                for (let i = 0; i < js_shipping_rows.length; i++) {
                    if (i >= recommended_count) {
                        js_shipping_rows[i].classList.add('tv-display__none');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__even');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__odd');
                    } else {
                        js_shipping_rows[i].classList.add(i % 2 ? 'tv-zebra-table__even' : 'tv-zebra-table__odd');
                        js_shipping_rows[i].classList.remove('tv-display__none');
                    }
                }

                js_rec_options.classList.add('tv-shipping-selected');
                js_ups_options.classList.remove('tv-shipping-selected');
                js_fedex_options.classList.remove('tv-shipping-selected');
                js_usps_options.classList.remove('tv-shipping-selected');

                const dialog = $.ceDialog('get_last')[0];
                if (dialog) {
                    $.ceDialog('get_last').ceDialog('reload');
                }
            });

            // UPS Options.
            js_ups_options.addEventListener('click', function () {
                let displayCount = 0;
                for (let i = 0; i < js_shipping_rows.length; i++) {
                    if (js_shipping_rows[i].lastElementChild.innerHTML.includes('UPS')) {
                        js_shipping_rows[i].classList.remove('tv-display__none');
                        js_shipping_rows[i].classList.add(
                            (displayCount % 2) ? 'tv-zebra-table__even' : 'tv-zebra-table__odd'
                        );
                        displayCount++;
                    } else {
                        js_shipping_rows[i].classList.add('tv-display__none');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__even');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__odd');
                    }
                }

                js_rec_options.classList.remove('tv-shipping-selected');
                js_ups_options.classList.add('tv-shipping-selected');
                js_fedex_options.classList.remove('tv-shipping-selected');
                js_usps_options.classList.remove('tv-shipping-selected');

                const dialog = $.ceDialog('get_last')[0];
                if (dialog) {
                    $.ceDialog('get_last').ceDialog('reload');
                }
            });

            // FedEx Options.
            js_fedex_options.addEventListener('click', function () {
                let displayCount = 0;
                for (let i = 0; i < js_shipping_rows.length; i++) {
                    if (js_shipping_rows[i].lastElementChild.innerHTML.includes('FedEx')) {
                        js_shipping_rows[i].classList.remove('tv-display__none');
                        js_shipping_rows[i].classList.add(
                            (displayCount % 2) ? 'tv-zebra-table__even' : 'tv-zebra-table__odd'
                        );
                        displayCount++;
                    } else {
                        js_shipping_rows[i].classList.add('tv-display__none');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__even');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__odd');
                    }
                }

                js_rec_options.classList.remove('tv-shipping-selected');
                js_ups_options.classList.remove('tv-shipping-selected');
                js_fedex_options.classList.add('tv-shipping-selected');
                js_usps_options.classList.remove('tv-shipping-selected');

                const dialog = $.ceDialog('get_last')[0];
                if (dialog) {
                    $.ceDialog('get_last').ceDialog('reload');
                }
            });

            // USPS Options.
            js_usps_options.addEventListener('click', function () {
                let displayCount = 0;
                for (let i = 0; i < js_shipping_rows.length; i++) {
                    if (js_shipping_rows[i].lastElementChild.innerHTML.includes('USPS')) {
                        js_shipping_rows[i].classList.remove('tv-display__none');
                        js_shipping_rows[i].classList.add(
                            (displayCount % 2) ? 'tv-zebra-table__even' : 'tv-zebra-table__odd'
                        );
                        displayCount++;
                    } else {
                        js_shipping_rows[i].classList.add('tv-display__none');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__even');
                        js_shipping_rows[i].classList.remove('tv-zebra-table__odd');
                    }
                }

                js_rec_options.classList.remove('tv-shipping-selected');
                js_ups_options.classList.remove('tv-shipping-selected');
                js_fedex_options.classList.remove('tv-shipping-selected');
                js_usps_options.classList.add('tv-shipping-selected');

                const dialog = $.ceDialog('get_last')[0];
                if (dialog) {
                    $.ceDialog('get_last').ceDialog('reload');
                }
            });

            const dialog = $.ceDialog('get_last')[0];
            if (dialog) {
                $.ceDialog('get_last').ceDialog('reload');
            }
            this.toggle_inputs();
        });
        if (document.getElementById('js-delivery-address-warning')) {
            document.getElementById('js-delivery-address-warning').classList.add('tv-display__none');
        }
        if (document.getElementById('js-name-phone-container')) {
            document.getElementById('js-name-phone-container').classList.add('hidden');
        }
        this.elements.production_and_shipping_main_container.classList.remove('hidden');
        this.elements.address_and_carrier_locations_container.classList.add('hidden');
        this.shipping_estimator.elements.results_container.classList.remove('hidden');
        this.resize();
    }

    /**
     * Validate the required fields.
     *
     * @return {boolean}
     */
    protected validate_required_fields(required_fields: Array<string>): boolean {
        // Validations for required fields without cm-required.
        let valid = true;
        let message = '';

        const address = this.address;
        for (const field of required_fields) {
            let field_name = field;
            if (field === 'zipcode') {
                field_name = 'zipcode_input';
            }

            this.elements[field_name].classList.remove('tv-error-highlight-border');
            if (address[field] === '') {
                this.elements[field_name].focus();
                this.elements[field_name].classList.add('tv-error-highlight-border');
                const temp_name = field === 'address1' ? 'Address' : field;
                const str_name = temp_name.charAt(0).toUpperCase() + temp_name.slice(1);
                message += `- ${str_name} <br/>`;
                valid = false;
            }
        }

        if (!valid) {
            const error_msg = 'Error: Please enter your address in the required fields.';
            document.getElementById('js-delivery-address-validation').innerHTML = error_msg;
            document.getElementById('js-delivery-address-validation').classList.remove('tv-display__none');
            this.resize();

            return false;
        }

        // Hide the validation message.
        document.getElementById('js-delivery-address-validation').classList.add('hidden');
        document.getElementById('js-delivery-address-validation').innerHTML = '';

        return true;
    }

    /**
     * Handles the keyup and change events on the zipcode input.
     *
     * @returns {void}
     */
    protected zipcode_input(): void {
        // Throttle user inputs so that if they are rapidly making changes to a control we don't send ajax too soon.
        if (this.throttle || this.elements.zipcode_input.value.length < 5) {
            clearTimeout(this.throttle);
        }

        if (this.elements.zipcode_input.value.length < 5) {
            return;
        }

        // ts-check thinks this is a NodeJS.Timer and webpack thinks it's a number so some casting to band-aid here.
        this.throttle = <number><any>setTimeout(
            this.estimate_back_to_shipping_summary.bind(this),
            200
        );
    }

    /**
     * Callback for when a production time is selected. It will invoke the shipping estimator.
     *
     * @param {HTMLElement} production_code The production code element passed by the selector.
     * @returns {void}
     */
    protected select_production_time_option(production_code: HTMLElement): void {
        if (this.shipping_estimator.fetching) {
            return;
        }

        this.production_code = production_code.dataset.value;
        this.production_price = parseFloat(production_code.dataset.price);
        if (!this.estimator_opened) {
            return;
        }

        this.production_flex_grid.locked = true;
        this.toggle_inputs();
        this.shipping_estimator.fetch_estimates(this.address, this.production_code).then(() => {
            this.production_flex_grid.locked = false;
            this.toggle_inputs();
        });

        // Fill out the summary selected production time element.
        const selected_production_time_option = this.production_flex_grid.selected_element();
    }

    /**
     * Format the production times into a table layout.
     * Note: The setup call will select the first element in the grid which will trigger a shipping estimation.
     *
     * @return {void}
     */
    protected render_production_times(): void {
        const production_times = JSON.parse(this.elements.production_times.value);

        // Replace 0.00 with "Free"
        for (const [key, value] of Object.entries(production_times)) {
            if (production_times[key].price === '0.00' || production_times[key].price == '0') {
                production_times[key].display_price = "FREE";
            } else {
                production_times[key].display_price = '$' + production_times[key].price;
            }
        }

        const config_type_input = <HTMLInputElement>(this.elements.order_type);
        let cutofftime_class = 'tv-production-option-approval-time';
        if (config_type_input && config_type_input.value === 'HardwareOnly') {
            cutofftime_class = 'tv-production-option-hardware-approval-time';
        }

        const element_options = {
            innerHTML: new Map([
                ['name', 'tv-production-option-name'],
                ['price', 'hidden'],
                ['display_price', 'tv-production-option-price'],
                ['cutofftime_display', cutofftime_class]
            ]),
            dataValues: [
                'value',
                'price'
            ],
            className: ['js-production-time-option']
        };

        // Create rows for each production time
        for (let i = 0; i < production_times.length; i++) {

            // If price is "$0", display as "FREE"
            if (production_times[i].display_price == '$0') {
                production_times[i].display_price = "FREE";
            }

            // Populate production time rows with data
            this.elements.production_times_container.innerHTML +=
                "<tr height='35' class='js-production-time-option js-production-table-row'><td>" +
                "<input type='radio' class='js-production-time-radio-btn'></td><td>" +
                production_times[i].name + "</td><td>" +
                production_times[i].display_price + "</td><td>" +
                production_times[i].cutofftime_display + "</td><td>" +
                "</td></tr>";
            document.getElementsByClassName('js-production-time-option')[i].setAttribute(
                "data-value",
                production_times[i].value
            );
            document.getElementsByClassName('js-production-time-option')[i].setAttribute(
                "data-price",
                production_times[i].price
            );
        }

        const js_production_options = Array.from(document.getElementsByClassName('js-production-time-option'));
        const js_production_time_radio_btn = Array.from(document.getElementsByClassName('js-production-time-radio-btn'));
        js_production_options[0].classList.add('tv-flex-grid-box__selected');
        js_production_time_radio_btn[0].setAttribute('checked', '');

        // Click functionality for each row class
        for (let i = 0; i < js_production_options.length; i++) {
            js_production_options[i].addEventListener('click', function () {
                // Activate hidden shipping production elements on click
                js_production_options.forEach(production_option => {
                    production_option.classList.remove('tv-flex-grid-box__selected');
                });
                js_production_time_radio_btn.forEach(radio_btn => {
                    radio_btn.removeAttribute('checked');
                });
                js_production_options[i].classList.add('tv-flex-grid-box__selected');
                js_production_time_radio_btn[i].setAttribute('checked', '');
                (<HTMLElement>document.getElementsByClassName('tv-flex-grid-box')[i]).click();
                document.getElementById('js-shipping-rec-options-btn').click();
            });
        }

        // Remove these from the view if there is only 1 options.
        if (Object.keys(production_times).length <= 1) {
            if (this.elements.see_more_production_options_link) {
                this.elements.see_more_production_options_link.classList.add('hidden');
            }
        } else {
            // Otherwise, add them back in.
            if (this.elements.see_more_production_options_link) {
                this.elements.see_more_production_options_link.classList.remove('hidden');
            }
        }

        this.production_flex_grid = new FlexGridBoxes(
            production_times,
            element_options,
            true,
            this.elements.production_times_container,
            this.select_production_time_option.bind(this)
        );

        this.production_flex_grid.setup();

        // Fill out the summary selected production time element.
        const selected_production_time_option = this.production_flex_grid.selected_element();

    }

    /**
     * Called when an address book entry is selected.
     *
     * @param {IAddress} selected_address The address that was selected from the address book.
     * @returns {void}
     */
    protected address_book_callback(selected_address: IAddress): void {
        this.address = selected_address;
        this.elements.zipcode_input.value = this.address.zipcode;
    }

    /**
     * Resizes the modal.
     *
     * @returns {void}
     */
    protected resize(): void {
        $.ceDialog('get_last').ceDialog('reload');
    }
}
